
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Company, Filter, Video, FilterType, VideoStatus, AiAnalysis, KpiDefinition } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { AuthContext } from '../../contexts/AuthContext';
import Spinner from '../../components/common/Spinner';
import { generateVideoAnalysis } from '../../services/geminiService';
import { Timestamp } from 'firebase/firestore';

// Reduced max frames further to ensure stability
const MAX_FRAMES = 15; 

const AddVideoPage: React.FC = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [thumbnailUrl, setThumbnailUrl] = useState('');
    const [filterValues, setFilterValues] = useState<Record<string, string | number | string[]>>({});
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState('');
    
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const tenantId = authContext?.user?.tenantId;

    useEffect(() => {
        const fetchCompanies = async () => {
            setLoading(true);
            if (tenantId) {
                const fetchedCompanies = await firestoreService.getCompanies(tenantId);
                setCompanies(fetchedCompanies);
            }
            setLoading(false);
        };
        fetchCompanies();
    }, [tenantId]);

    useEffect(() => {
        const fetchFilters = async () => {
            if (!selectedCompanyId || !tenantId) {
                setFilters([]);
                setFilterValues({});
                return;
            }
            const fetchedFilters = await firestoreService.getFilters(tenantId, selectedCompanyId);
            setFilters(fetchedFilters);
            const initialValues: Record<string, string | number | string[]> = {};
            fetchedFilters.forEach(f => {
                if (f.type === FilterType.MULTI) {
                    initialValues[f.name] = [];
                } else {
                    initialValues[f.name] = '';
                }
            });
            setFilterValues(initialValues);
        };
        fetchFilters();
    }, [selectedCompanyId, tenantId]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setVideoFile(file);
            // Generate a thumbnail from the first frame
            generateThumbnail(file, (dataUrl) => {
                setThumbnailUrl(dataUrl);
            });
        }
    };

    const generateThumbnail = (file: File, callback: (dataUrl: string) => void) => {
        const video = document.createElement('video');
        const objectUrl = URL.createObjectURL(file);
        video.src = objectUrl;
        video.muted = true;
        video.playsInline = true;

        video.onloadeddata = () => {
            video.currentTime = 1; // Seek to 1 second
        };
        video.onseeked = () => {
            const canvas = document.createElement('canvas');
            // Resize thumbnail for performance
            const scale = Math.min(1, 480 / video.videoWidth);
            canvas.width = video.videoWidth * scale;
            canvas.height = video.videoHeight * scale;
            
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                callback(canvas.toDataURL('image/jpeg', 0.7));
            }
            URL.revokeObjectURL(objectUrl);
        };
    };

    const extractFramesFromFile = (file: File): Promise<string[]> => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const fileUrl = URL.createObjectURL(file);
            video.src = fileUrl;
            video.muted = true;
            video.playsInline = true;
            
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const frames: string[] = [];
            const MAX_WIDTH = 640; 

            const cleanup = () => {
                video.pause();
                video.removeAttribute('src');
                video.load();
                setTimeout(() => {
                    URL.revokeObjectURL(fileUrl);
                }, 5000);
            };

            video.onloadedmetadata = () => {
                let width = video.videoWidth;
                let height = video.videoHeight;
                if (width > MAX_WIDTH) {
                    const ratio = MAX_WIDTH / width;
                    width = MAX_WIDTH;
                    height = height * ratio;
                }

                canvas.width = width;
                canvas.height = height;
                
                const duration = video.duration;
                const interval = Math.max(0.5, duration / MAX_FRAMES);
                
                let currentTime = 0;
                let capturedFrames = 0;

                const captureFrame = () => {
                    if (!ctx) {
                        cleanup();
                        return reject(new Error("Canvas context is not available."));
                    }
                    
                    try {
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        const frameDataUrl = canvas.toDataURL('image/jpeg', 0.5);
                        frames.push(frameDataUrl.split(',')[1]); 
                        capturedFrames++;
                    } catch (e) {
                        console.warn("Error drawing frame:", e);
                    }

                    currentTime += interval;

                    if (capturedFrames >= MAX_FRAMES || currentTime >= duration) {
                        cleanup();
                        resolve(frames);
                        return;
                    }

                    if (currentTime > duration) currentTime = duration;
                    video.currentTime = currentTime;
                };

                video.onseeked = captureFrame;
                video.onerror = (e) => {
                    cleanup();
                    reject(new Error("Video playback error during frame extraction"));
                };
                
                video.currentTime = 0; 
            };

            video.onerror = (e) => {
                cleanup();
                reject(new Error("Error loading video file for frame extraction."));
            };
        });
    };
    
    const handleFilterValueChange = (filterName: string, value: string | number) => {
        setFilterValues(prev => ({ ...prev, [filterName]: value }));
    };

    const handleMultiFilterChange = (filterName: string, option: string) => {
        setFilterValues(prev => {
            const existingValues = (prev[filterName] as string[] | undefined) || [];
            const newValues = existingValues.includes(option)
                ? existingValues.filter(item => item !== option) 
                : [...existingValues, option]; 
            return { ...prev, [filterName]: newValues };
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedCompanyId || !title || !authContext?.user || !videoFile || !videoUrl || !tenantId) {
            alert('Please select a video file, provide a video URL, and fill all required fields.');
            return;
        }

        setIsSubmitting(true);
        try {
            setSubmissionStatus('Configurando análisis...');
            const kpiProfile = await firestoreService.getCompanyKpiProfile(selectedCompanyId);
            const customKpis = kpiProfile?.kpis;

            setSubmissionStatus('Extrayendo fotogramas del video...');
            await new Promise(r => setTimeout(r, 100));
            
            const frames = await extractFramesFromFile(videoFile);
            
            if (frames.length === 0) {
                throw new Error("No se pudieron extraer imágenes del video.");
            }

            setSubmissionStatus(`Analizando ${frames.length} imágenes con IA...`);
            
            // Fixed: Removed manualApiKey parameter as per guidelines. API Key is obtained from environment.
            const kpis = await generateVideoAnalysis(frames, customKpis);

            setSubmissionStatus('Guardando video y análisis...');
            const newVideoData: Omit<Video, 'videoId' | 'createdAt'> = {
                companyId: selectedCompanyId,
                tenantId: tenantId,
                title,
                description,
                storagePath: videoUrl,
                filters: filterValues,
                status: VideoStatus.PROCESSING, 
                aiAnalysisId: null,
                uploadedBy: authContext.user.uid,
                thumbnailUrl,
            };
            const addedVideo = await firestoreService.addVideo(newVideoData);

            const newAnalysisData: Omit<AiAnalysis, 'analysisId' | 'generatedAt'> = {
                videoId: addedVideo.videoId,
                tenantId: tenantId,
                companyId: selectedCompanyId,
                kpis,
                versions: [{ v: 1, editor: 'video-local-ai', timestamp: Timestamp.now(), kpis }]
            };
            // Fixed: Passed tenantId as first argument to saveAnalysis.
            await firestoreService.saveAnalysis(tenantId, newAnalysisData);

            alert('Video and AI analysis saved successfully!');
            navigate(`/videos/company/${selectedCompanyId}`);
        } catch (error) {
            console.error("Submission error:", error);
            alert(`Error: ${(error as Error).message}`);
        } finally {
            setIsSubmitting(false);
            setSubmissionStatus('');
        }
    };
    
    const renderFilterInput = (filter: Filter) => {
        const value = filterValues[filter.name] || '';
        switch (filter.type) {
            case FilterType.TEXT:
                return <input type="text" value={value as string} onChange={e => handleFilterValueChange(filter.name, e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />;
            case FilterType.NUMBER:
                return <input type="number" value={value as number} onChange={e => handleFilterValueChange(filter.name, e.target.valueAsNumber)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />;
            case FilterType.DATE:
                return <input type="date" value={value as string} onChange={e => handleFilterValueChange(filter.name, e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />;
            case FilterType.MULTI:
                return (
                    <div className="flex flex-col gap-2 p-2 bg-gray-800 rounded-md border border-gray-700">
                        {filter.options?.map(opt => (
                            <label key={opt} className="flex items-center gap-2 text-gray-300 px-1">
                                <input
                                    type="checkbox"
                                    checked={((filterValues[filter.name] as string[]) || []).includes(opt)}
                                    onChange={() => handleMultiFilterChange(filter.name, opt)}
                                    className="h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                                />
                                {opt}
                            </label>
                        ))}
                    </div>
                );
            case FilterType.DROPDOWN:
                return (
                    <select value={value as string} onChange={e => handleFilterValueChange(filter.name, e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">Select an option</option>
                        {filter.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                );
            default:
                return null;
        }
    };


    if (loading) return <div className="flex justify-center items-center h-full"><Spinner size="lg" /></div>;

    return (
        <div>
            <Link to="/videos" className="text-blue-400 hover:text-blue-300 mb-6 inline-flex items-center gap-2">
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                 Back to Videos
            </Link>
            <h1 className="text-3xl font-bold text-white mb-6">Upload & Analyze New Video</h1>

            <form onSubmit={handleSubmit} className="space-y-6 bg-gray-900 p-8 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                        <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)} required className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="" disabled>Select a company</option>
                            {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                        </select>
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Video Title</label>
                        <input type="text" value={title} onChange={e => setTitle(e.target.value)} required className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Video URL (for playback)</label>
                    <input 
                        type="url" 
                        value={videoUrl} 
                        onChange={e => setVideoUrl(e.target.value)} 
                        required 
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., https://drive.google.com/file/d/.../preview"
                    />
                    <p className="text-xs text-gray-500 mt-1">Provide the public link to the video file. For Google Drive, use the 'preview' link.</p>
                </div>

                 <div>
                    <label htmlFor="video-file-upload" className="block text-sm font-medium text-gray-300 mb-2">Video File (for AI Analysis)</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-700 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                             {thumbnailUrl ? (
                                <img src={thumbnailUrl} alt="Video thumbnail" className="mx-auto h-24 w-auto rounded-md" />
                            ) : (
                                <svg className="mx-auto h-12 w-12 text-gray-500" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4 4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                            <div className="flex text-sm text-gray-500">
                                <label htmlFor="video-file-upload" className="relative cursor-pointer bg-gray-800 rounded-md font-medium text-blue-400 hover:text-blue-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-gray-900 focus-within:ring-blue-500 px-1">
                                    <span>Upload a file</span>
                                    <input id="video-file-upload" name="video-file-upload" type="file" className="sr-only" accept="video/*" onChange={handleFileChange} required />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-600">{videoFile ? videoFile.name : 'MP4, MOV, AVI up to 500MB'}</p>
                        </div>
                    </div>
                </div>
               
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" rows={2}></textarea>
                </div>

                {filters.length > 0 && (
                    <div className="border-t border-gray-800 pt-6">
                         <h2 className="text-xl font-semibold text-white mb-4">Filters</h2>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {filters.map(filter => (
                                <div key={filter.filterId}>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">{filter.name}</label>
                                    {renderFilterInput(filter)}
                                </div>
                            ))}
                         </div>
                    </div>
                )}
                
                <div className="flex justify-end pt-4">
                    <button type="submit" disabled={isSubmitting || !videoFile} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:bg-gray-500 disabled:cursor-not-allowed flex items-center gap-2">
                        {isSubmitting && <Spinner size="sm"/>}
                        {isSubmitting ? submissionStatus : 'Upload & Analyze Video'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddVideoPage;
